import React from 'react';
import { Menu, Edit2, Trash, Clock, ArrowRightCircle, ArrowDownCircle, Users } from 'lucide-react';
import { formatarPreco } from '../../../utils/priceUtils';
import { hasActiveCustomizations } from '../../../utils/serviceUtils';

/**
 * Componente que renderiza um cartão de serviço com drag and drop
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.service - Dados do serviço
 * @param {Function} props.onEdit - Função chamada ao editar o serviço
 * @param {Function} props.onDelete - Função chamada ao excluir o serviço
 */
const ServiceCard = ({ service, onEdit, onDelete }) => {
  if (!service) return null;
  
  // Verificar se há realmente valores personalizados ativos (não apenas presentes)
  const hasCustomPricing = hasActiveCustomizations(service.professionalCustomizations);
  
  return (
    <div className="p-1.5 rounded-lg border border-gray-100 bg-white shadow-sm hover:shadow transition-all duration-200 group select-none touch-manipulation">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-start">
            <div className="mt-0.5 cursor-move text-gray-300 group-hover:text-gray-500 transition-colors">
              <Menu size={15} />
            </div>
            
            <div className="flex-1 select-none ml-2">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900 text-sm select-none">{service.nome || 'Sem nome'}</h3>
                {/* Mostrar label apenas se houver valores personalizados ativos */}
                {hasCustomPricing && (
                  <div className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-medium">
                    <Users size={10} className="mr-0.5" />
                    <span>Preços personalizados</span>
                  </div>
                )}
              </div>
              
              {service.descricao && (
                <p className="text-xs text-gray-500 line-clamp-1 mb-1 select-none">{service.descricao}</p>
              )}
              
              <div className="flex flex-wrap gap-1.5 mt-1">
                <div className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium select-none">
                  {formatarPreco(service.preco || 0)}
                </div>
                
                <div className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-50 text-gray-700 text-xs select-none">
                  <Clock size={12} className="mr-0.5" />
                  <span>{service.duracao || 0} min</span>
                </div>
                
                {service.setupTime > 0 && (
                  <div className="inline-flex items-center px-1.5 py-0.5 rounded bg-green-50 text-green-700 text-xs select-none">
                    <ArrowDownCircle size={10} className="mr-0.5" />
                    <span>Preparo: {service.setupTime} min</span>
                  </div>
                )}
                
                {service.finishTime > 0 && (
                  <div className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-xs select-none">
                    <ArrowRightCircle size={10} className="mr-0.5" />
                    <span>Final: {service.finishTime} min</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(service)}
            className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
            aria-label="Editar"
          >
            <Edit2 size={13} />
          </button>
          <button
            onClick={() => onDelete(service)}
            className="p-1 rounded-full bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
            aria-label="Excluir"
          >
            <Trash size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceCard; 